import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Snackbar, TextField, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import statsDefault from "../data/statsDefault.json";
import { uploadFile } from "../utilities/updateS3";
import PasswordModal from "./PasswordModal";

function mergeStats(defaults, fetched) {
  const mergeArray = (defaultArr, fetchedArr = []) => {
    const fetchedMap = Object.fromEntries(fetchedArr.map((s) => [s.label, s.value]));
    return defaultArr.map((s) => ({
      ...s,
      value: s.label in fetchedMap ? fetchedMap[s.label] : s.value,
    }));
  };
  return {
    ...defaults,
    ...fetched,
    allTime: mergeArray(defaults.allTime, fetched.allTime),
    yearStats: mergeArray(defaults.yearStats, fetched.yearStats),
  };
}

const UpdateStats = () => {
  const history = useHistory();
  const [stats, setStats] = useState(statsDefault);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertStatus, setAlertStatus] = useState("");
  const [alertText, setAlertText] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(
          "https://plantbuilt.s3.us-west-2.amazonaws.com/stats.json",
          { cache: "no-cache" }
        );
        if (response.ok) {
          const data = await response.json();
          setStats(mergeStats(statsDefault, data));
        }
      } catch (e) {
        console.error("Could not load stats from S3, using fallback.", e);
      }
    })();
  }, []);

  const handleAllTimeChange = useCallback((idx, rawValue) => {
    const value = rawValue === "" ? "" : Number(rawValue);
    setStats((prev) => {
      const allTime = prev.allTime.map((stat, i) =>
        i === idx ? { ...stat, value } : stat
      );
      return { ...prev, allTime };
    });
  }, []);

  const handleYearStatChange = useCallback((idx, rawValue) => {
    const value = rawValue === "" ? "" : Number(rawValue);
    setStats((prev) => {
      const yearStats = prev.yearStats.map((stat, i) =>
        i === idx ? { ...stat, value } : stat
      );
      return { ...prev, yearStats };
    });
  }, []);

  const handleYearChange = useCallback((e) => {
    const currentYear = e.target.value;
    setStats((prev) => ({ ...prev, currentYear }));
  }, []);

  const submitStats = useCallback(async () => {
    try {
      const response = await uploadFile(password, "stats", JSON.stringify(stats));
      if (response.status === 200) {
        setAlertStatus("success");
        setShowAlert(true);
        setAlertText("Team stats have been updated.");
      } else {
        setAlertStatus("error");
        setShowAlert(true);
        setAlertText("There was an issue updating stats. Please try again.");
      }
    } catch (e) {
      setAlertStatus("error");
      setShowAlert(true);
      setAlertText("There was an issue updating stats. Please try again.");
    }
  }, [password, stats]);

  const fieldStyle = { marginBottom: "16px", width: "100%" };

  return (
    <div style={{ maxWidth: "600px", margin: "32px auto", padding: "0 16px" }}>
      {showAlert && (
        <Snackbar
          open={showAlert}
          autoHideDuration={6000}
          onClose={() => setShowAlert(false)}
        >
          <Alert
            onClose={() => setShowAlert(false)}
            severity={alertStatus || "info"}
            variant="filled"
          >
            {alertText}
          </Alert>
        </Snackbar>
      )}

      <Button
        onClick={() => history.push("/athlete-portal")}
        style={{ marginBottom: "24px" }}
      >
        ← Back
      </Button>

      <Typography variant="h5" style={{ marginBottom: "24px", fontFamily: "Impact" }}>
        Update Team Stats
      </Typography>

      {/* Current Year */}
      <Typography variant="subtitle1" style={{ marginBottom: "8px", color: "#709425" }}>
        Current Year
      </Typography>
      <TextField
        label="Current Year"
        value={stats.currentYear}
        onChange={handleYearChange}
        style={fieldStyle}
      />

      {/* All-Time Stats */}
      <Typography
        variant="subtitle1"
        style={{ marginTop: "16px", marginBottom: "8px", color: "#709425" }}
      >
        All-Time Stats
      </Typography>
      {stats.allTime.map((stat, idx) => (
        <TextField
          key={idx}
          label={stat.label}
          type="number"
          value={stat.value}
          onChange={(e) => handleAllTimeChange(idx, e.target.value)}
          style={fieldStyle}
          inputProps={{ min: 0 }}
        />
      ))}

      {/* Year Stats */}
      <Typography
        variant="subtitle1"
        style={{ marginTop: "16px", marginBottom: "8px", color: "#709425" }}
      >
        {stats.currentYear} Stats
      </Typography>
      {stats.yearStats.map((stat, idx) => (
        <TextField
          key={idx}
          label={stat.label}
          type="number"
          value={stat.value}
          onChange={(e) => handleYearStatChange(idx, e.target.value)}
          style={fieldStyle}
          inputProps={{ min: 0 }}
        />
      ))}

      <Button
        variant="contained"
        color="success"
        fullWidth
        style={{ marginTop: "24px" }}
        onClick={() => setShowPasswordModal(true)}
      >
        Save and Submit
      </Button>

      <PasswordModal
        showPasswordModal={showPasswordModal}
        hideModal={() => setShowPasswordModal(false)}
        password={password}
        setPassword={setPassword}
        submitProfile={submitStats}
      />
    </div>
  );
};

export default UpdateStats;
